import { type FC, type MouseEventHandler, useMemo } from 'react';
import { BlockBody, handleReplaceMentionValues } from '@ContractBuilder/modules/block';
import type { DbSubmissionBlock } from '@domain/schemas/block.schema';
import { injectThreadMarks, parseThreadMarkIds, THREAD_MARK_SELECTOR, type ThreadMarkInput } from '@features/comments';
import { sanitizeHtml } from '@helpers/sanitizeHtml';
import { isTwoColLayoutType } from '@root/helpers/blocks';
import type { BlockMentionValues } from '@root/helpers/blocks/get-block-mention-values';
import { isTemplatePath } from '@utils/app-paths';

import { blockContentClasses, blockTitleClasses } from '../constants/classes';

interface BlockViewProps {
  block: DbSubmissionBlock;
  className?: string;
  disableMentionHighlight?: boolean;
  hasBeenMovedByRenewal?: boolean;
  isContinuation?: boolean;
  isEditing?: boolean;
  isHighlighted?: boolean;
  isInCreateMode?: boolean;
  isPdfRender?: boolean;
  isVisible?: boolean;
  mentionValues: BlockMentionValues;
  showEndorsementDiff?: boolean;
  anchorOffset?: number;
  threadAnchors?: ThreadMarkInput[];
  /** Content whose offsets do not match the stored block (e.g. a previewed variation) must not start or show threads. */
  inlineCommentsDisabled?: boolean;
  onThreadMarkClick?: (threadIds: string[]) => void;
}

export const BlockContent: FC<BlockViewProps> = ({
  block,
  className,
  disableMentionHighlight,
  hasBeenMovedByRenewal = false,
  isContinuation = false,
  isEditing,
  isHighlighted,
  isInCreateMode,
  isPdfRender,
  isVisible,
  mentionValues,
  showEndorsementDiff,
  anchorOffset,
  threadAnchors,
  inlineCommentsDisabled = false,
  onThreadMarkClick,
}) => {
  const isTemplate = isTemplatePath();

  const { title, content = '', order, type, id, name, canEdit } = block;

  const isLockedFromEditing = !isTemplate && !canEdit;
  const isTwoColLayout = isTwoColLayoutType(type);
  const disableMentions = isTemplate;

  const processedContent = isPdfRender ? content : handleReplaceMentionValues(block, mentionValues);

  const html = useMemo(() => {
    const sanitized = isVisible ? sanitizeHtml(processedContent ?? content) : '';
    return !isPdfRender && !inlineCommentsDisabled && threadAnchors?.length
      ? injectThreadMarks(sanitized, threadAnchors)
      : sanitized;
  }, [isVisible, processedContent, content, isPdfRender, inlineCommentsDisabled, threadAnchors]);
  const hasInlineComments = !isPdfRender && !inlineCommentsDisabled;

  const handleContentClick: MouseEventHandler<HTMLDivElement> = (event) => {
    const mark = (event.target as Element).closest?.(THREAD_MARK_SELECTOR);
    if (mark && onThreadMarkClick) {
      // Keep the block's own click (enter edit mode in Draft) from firing for a mark click.
      event.stopPropagation();
      onThreadMarkClick(parseThreadMarkIds(mark));
    }
  };

  return (
    <BlockBody
      className={className}
      id={isContinuation ? undefined : id}
      name={name}
      order={order}
      // flags
      disableMentionHighlight={disableMentionHighlight}
      hasBeenMovedByRenewal={hasBeenMovedByRenewal}
      isPDFRender={isPdfRender}
      isEditing={isEditing}
      isHighlighted={isHighlighted}
      isVisible={isVisible}
      isInCreateMode={isInCreateMode}
      isLockedFromEditing={isLockedFromEditing}
      showEndorsementDiff={showEndorsementDiff}
    >
      {isTwoColLayout && (
        <div className={blockTitleClasses({ type, pdf: isPdfRender && type })}>{!isContinuation && title}</div>
      )}

      <div
        dangerouslySetInnerHTML={{ __html: html }}
        className={blockContentClasses({
          type,
          pdf: isPdfRender && type,
          disableMentions,
        })}
        data-block-content={hasInlineComments ? id : undefined}
        data-block-content-offset={hasInlineComments ? (anchorOffset ?? 0) : undefined}
        onClick={handleContentClick}
      />
    </BlockBody>
  );
};
